// Creating a split, one question per screen: check the bill → how many people → the tip →
// (first time only) where friends pay you. Then the payer's page takes over: share, your items, get paid.
import { UNNAMED_VENUE } from '../../venue-name.ts';
import { fmt, offFromTotal, plain } from '../../money.ts';
import { titled } from '../../brand.ts';
import type { Cents, ParsedReceipt, Place } from '../../types.ts';
import { checkCode, createSplit, parseReceipt, type PayerDetails } from '../api.ts';
import { app, clampSize, esc, icon, leadCents, openSheet, stepBar, stepper, target, toCents, toast, todayLabel } from '../dom.ts';
import { go } from '../router.ts';
import { saved } from '../saved.ts';
import { feeArm, linkCode, trackStep } from '../track.ts';
import { fee, payerFeeNote } from '../fee.ts';
import { FEE_MIN_TABLE, thanksSwitchShown } from '../../experiments.ts';
import { NAME_TOO_SHORT, nameLongEnough } from '../../names.ts';

/** From /api/config. `sms`: our receipt and reminder texts are on (SMS_REMINDERS, smsEnabled() in src/sms.ts). */
export const config = { receiptReading: false, sms: false };

interface DraftItem { key: string; name: string; cents: Cents; maybeFee?: boolean }
// A percent tip is worked out on the food alone or on the total with tax, whichever the payer tips on.
type Tip = { mode: 'pct'; pct: number; on: 'food' | 'total'; label: string } | { mode: 'amount'; cents: Cents; label: string };

interface Draft {
  step: 'scanning' | 'review' | 'people' | 'tip' | 'pay';
  venue: string;
  date: string;
  items: DraftItem[];
  taxCents: Cents;
  /** Charges everyone shares like tax, such as a card surcharge. Not claimed by anyone. */
  fees: DraftItem[];
  tip: Tip;
  /** Where the tip came from: read off the receipt, picked by the host, or "No tip". */
  tipSource: 'receipt' | 'host_pick' | 'no_tip';
  // What the receipt printed, to check the lines against. Not edited by the payer.
  receipt: { subtotalCents: Cents | null; totalCents?: Cents | null; serviceCents?: Cents; printedTipCents?: Cents };
  photoUrl: string | null;
  /** The shrunk photo, kept so the payer can ask for it to be read again. */
  photo?: { data: string; type: string };
  /** The payer chose to leave a mismatch as is. */
  gapHidden?: boolean;
  error: string;
  note: string;
  setupError: string;
  /** The payer tapped "Change" on where friends pay them, so show that step even though it's saved. */
  editPay?: boolean;
  thanksOn: boolean;
  /** The host tapped the $1 thanks switch on themselves. */
  thanksPicked?: boolean;
  partySize: number;
  /** Split the whole bill evenly across partySize instead of everyone tapping items. */
  even: boolean;
  /** Where the restaurant is, read off the receipt. */
  place: Place | null;
  /** A promo or referral code: typed in, or brought by a link. `offer` is set once it checks out. */
  code: { value: string; open: boolean; offer?: string; fromLink?: boolean; checked?: boolean };
  /** For the stats: when the payer started, and how. */
  flow: { source: 'scan' | 'manual'; startedAt: number; rereads: number };
}

let draft: Draft | null = null;
let keySeq = 0;
const nextKey = () => 'i' + ++keySeq;
const blankItem = (): DraftItem => ({ key: nextKey(), name: '', cents: 0 });

function newDraft(step: Draft['step']): Draft {
  return {
    step, venue: '', date: todayLabel(),
    items: [blankItem()],
    taxCents: 0, fees: [], tip: { mode: 'pct', pct: 20, on: saved.tipOn(), label: '' }, tipSource: 'host_pick',
    receipt: { subtotalCents: null }, photoUrl: null, error: '', note: '', setupError: '',
    thanksOn: fee.pricing && feeArm() !== 'off', partySize: 2, even: false,
    flow: { source: step === 'scanning' ? 'scan' : 'manual', startedAt: Date.now(), rereads: 0 },
    code: { value: '', open: false },
    place: null,
  };
}

const subtotal = (d: Draft): Cents => d.items.reduce((a, it) => a + (it.cents || 0), 0);
/** What the restaurant charges before the tip: food, tax, fees and any service charge. */
const billBeforeTip = (d: Draft): Cents => subtotal(d) + d.taxCents + feesTotal(d) + (d.receipt.serviceCents || 0);
const tipBase = (d: Draft, on: 'food' | 'total'): Cents => (on === 'total' ? subtotal(d) + d.taxCents + feesTotal(d) : subtotal(d));
const tipCents = (d: Draft): Cents => (d.tip.mode === 'pct' ? Math.round((tipBase(d, d.tip.on) * d.tip.pct) / 100) : d.tip.cents);
/** What to write on the card slip. A service charge is already on the bill, so it isn't written again. */
const slip = (d: Draft): { tip: Cents; total: Cents } => {
  const tip = Math.max(0, tipCents(d) - (d.receipt.serviceCents || 0));
  return { tip, total: billBeforeTip(d) + tip };
};

/** One friend's share when splitting evenly: the whole bill over the table, rounded down like the server does. */
const evenEach = (d: Draft): Cents => (d.partySize >= 2 ? Math.floor((subtotal(d) + d.taxCents + feesTotal(d) + tipCents(d)) / d.partySize) : 0);

export function startManual(): void {
  draft = newDraft('review');
  trackStep('split_started', { source: 'manual' });
  go('/new');
}

/** Downscale a photo so uploads stay small on restaurant wifi. */
async function shrink(file: File): Promise<{ data: string; type: string }> {
  const url = URL.createObjectURL(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Couldn't open that photo. Try a different one, or type the items in."));
    i.src = url;
  });
  const scale = Math.min(1, 2000 / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * scale);
  c.height = Math.round(img.naturalHeight * scale);
  c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
  return { data: c.toDataURL('image/jpeg', 0.85).split(',')[1], type: 'image/jpeg' };
}

export async function startScan(file: File): Promise<void> {
  const mine = newDraft('scanning');
  mine.photoUrl = URL.createObjectURL(file);
  trackStep('split_started', { source: 'scan' });
  draft = mine;
  go('/new');
  if (!config.receiptReading) {
    mine.note = "Automatic reading isn't switched on yet, so type the items in. Your photo is above to read from.";
  } else {
    try {
      mine.photo = await shrink(file);
      applyReceipt(mine, await parseReceipt(mine.photo.data, mine.photo.type));
    } catch (err) {
      mine.error = (err as Error).message;
    }
  }
  mine.step = 'review';
  if (draft === mine && location.pathname === '/new') renderNew();
}

/** "Read it again" on the review screen: same photo, fresh reading, replacing the lines. */
async function rereadReceipt(d: Draft): Promise<void> {
  if (!d.photo) return;
  d.step = 'scanning';
  d.gapHidden = false;
  d.error = '';
  d.note = '';
  renderNew();
  try {
    const fresh = newDraft('review');
    applyReceipt(fresh, await parseReceipt(d.photo.data, d.photo.type));
    Object.assign(d, { ...fresh, photoUrl: d.photoUrl, photo: d.photo, thanksOn: d.thanksOn, thanksPicked: d.thanksPicked, flow: { ...d.flow, rereads: d.flow.rereads + 1 } });
  } catch (err) {
    d.error = (err as Error).message;
  }
  d.step = 'review';
  if (draft === d && location.pathname === '/new') renderNew();
}

/**
 * How far the lines are from what the receipt printed. Positive means the lines come up short.
 * Compares with the printed total when there is one (allowing for a printed tip inside it),
 * otherwise with the subtotal. Null when it adds up or nothing was printed to check against.
 */
const feesTotal = (d: Draft): Cents => d.fees.reduce((a, f) => a + f.cents, 0);

const addLink = (act: string, title: string, sub: string) => `
  <button class="link" data-act="${act}" style="display:block;text-align:left;text-decoration:none;padding:8px 0">
    <span style="color:var(--accent);font-weight:600">${title}</span>
    <span class="sub" style="display:block;font-size:13px">${sub}</span>
  </button>`;

interface Compare { diff: Cents; printed: Cents; ours: Cents; against: 'total' | 'subtotal'; off: boolean }

/** The lines next to what the receipt printed, whether or not they match. Null when nothing was printed. */
function receiptCompare(d: Draft): Compare | null {
  const sub = subtotal(d);
  const { subtotalCents, totalCents, serviceCents = 0, printedTipCents = 0 } = d.receipt;
  // The total catches what the subtotal can't, like a card surcharge, so it wins when printed.
  if (totalCents != null) {
    const options = [0, printedTipCents].map((t) => sub + d.taxCents + feesTotal(d) + serviceCents + t);
    const ours = options.reduce((best, x) => (Math.abs(totalCents - x) < Math.abs(totalCents - best) ? x : best));
    return { diff: totalCents - ours, printed: totalCents, ours, against: 'total', off: offFromTotal(ours, totalCents, d.items, subtotalCents) > 0 };
  }
  if (subtotalCents != null) return { diff: subtotalCents - sub, printed: subtotalCents, ours: sub, against: 'subtotal', off: subtotalCents !== sub };
  return null;
}

function receiptGap(d: Draft): Compare | null {
  const c = receiptCompare(d);
  return c?.off ? c : null;
}

/** Receipt says / we read, side by side, so "does it match?" is answered at a glance. */
const compareRows = (c: Compare, missingLabel: string) => `
  <div class="stack" style="gap:2px;font-size:16px">
    <div style="display:flex;justify-content:space-between"><span>Receipt ${c.against} says</span><b class="amt">${fmt(c.printed)}</b></div>
    <div style="display:flex;justify-content:space-between"><span>We read</span><b class="amt">${fmt(c.ours)}</b></div>
    ${missingLabel}
  </div>`;

/** The answer to "did we read it right?", at the top of Check the bill. Redrawn as lines are fixed. */
function renderGapBanner(d: Draft): void {
  const el = document.getElementById('gap');
  if (!el) return;
  const c = receiptCompare(d);
  if (!c) {
    // Nothing printed to check against (or typed in by hand): the payer is the check.
    el.innerHTML = `
      <div class="note" role="status">${d.photoUrl ? "We couldn't read a total on the receipt." : 'Type each line from your receipt.'} Your lines come to <b class="amt">${fmt(billBeforeTip(d))}</b> before the tip. Make sure that matches your receipt.</div>`;
    return;
  }
  if (!c.off) {
    el.innerHTML = `
      <div class="card stack" id="matchOk" style="border:2px solid var(--accent);background:var(--accent-wash);gap:8px" role="status">
        <b style="font-size:18px;color:var(--accent-deep)">${icon.check} Matches your receipt</b>
        ${compareRows(c, '')}
        <span class="sub">Still give the names and prices below a quick look.</span>
      </div>`;
    return;
  }
  if (d.gapHidden) {
    el.innerHTML = `<div class="alert" role="status">Doesn't match your receipt (off by ${fmt(Math.abs(c.diff))}). Continuing as is.</div>`;
    return;
  }
  const amt = fmt(Math.abs(c.diff));
  const option = (act: string, title: string, sub: string) => `
    <button data-act="${act}" style="display:block;width:100%;text-align:left;background:var(--card, #fff);color:var(--ink);border:1px solid var(--line, #e5e2dc);border-radius:12px;padding:10px 12px;cursor:pointer">
      <b style="display:block;font-size:15px">${title}</b>
      <span class="sub" style="display:block;font-size:13px">${sub}</span>
    </button>`;
  el.innerHTML = `
    <div class="alert" id="matchOff" style="flex-direction:column;align-items:stretch;gap:10px" role="status">
      <b style="font-size:18px">Doesn't match your receipt yet</b>
      ${compareRows(c, c.diff > 0
        ? `<div style="display:flex;justify-content:space-between"><span>Missing</span><b class="amt">${amt}</b></div>`
        : `<div style="display:flex;justify-content:space-between"><span>Too much</span><b class="amt">${amt}</b></div><span>Look for a line read twice or a wrong price.</span>`)}
      <div class="stack" style="gap:8px">
        ${c.diff > 0 && c.against === 'total' ? option('gapFee', `Add ${amt} as a fee`, 'Split across everyone by what they ordered, like tax. Best for a card surcharge or service fee.') : ''}
        ${c.diff > 0 ? option('gapLine', `Add a ${amt} line`, 'A new item someone claims, like any other. You can name it.') : ''}
        ${d.photo ? option('reread', 'Read it again', 'Claude takes another look at the photo. Takes a few seconds.') : ''}
        ${option('gapEdit', 'Fix it myself', 'Jump to the lines to change a name or price.')}
        ${option('gapHide', 'Leave it as is', 'Continue anyway. The split uses the lines exactly as they are now.')}
      </div>
    </div>`;
}

function applyReceipt(d: Draft, r: ParsedReceipt): void {
  d.venue = r.venue || '';
  d.place = r.place ?? null;
  if (r.date) d.date = r.date;
  if (r.items.length) d.items = r.items.map((it) => ({ key: nextKey(), name: it.name, cents: it.cents, maybeFee: it.maybeFee }));
  // Fees like a card surcharge are shared the same way tax is, in proportion to what each person had.
  d.taxCents = r.taxCents ?? 0;
  d.fees = r.fees?.length
    ? r.fees.map((f) => ({ key: nextKey(), name: f.name, cents: f.cents }))
    : r.feesCents ? [{ key: nextKey(), name: 'Fees on the receipt', cents: r.feesCents }] : [];
  d.receipt = { subtotalCents: r.subtotalCents, totalCents: r.totalCents, serviceCents: r.serviceChargeCents ?? 0, printedTipCents: r.tipCents ?? 0 };
  if (r.guests) d.partySize = r.guests;
  // A service charge and a tip written on top of it both go to the staff, so they add up.
  const svc = r.serviceChargeCents ?? 0;
  const written = r.tipCents ?? 0;
  if (svc || written) {
    const label = svc && written ? 'Service charge plus the tip written on the receipt' : svc ? 'Service charge on the receipt' : 'Tip written on the receipt';
    d.tip = { mode: 'amount', cents: svc + written, label };
    d.tipSource = 'receipt';
  }
  if (r.items.some((it) => it.fromQuantity)) d.note = 'Lines like "3 IPA" are split into single items so each person can claim their own.';
}

export function newView(): void {
  if (!draft) return go('/', true);
  renderNew();
}

function renderNew(): void {
  if (!draft) return;
  if (draft.step === 'scanning') return renderScanning(draft);
  if (draft.step === 'people') return renderPeople(draft);
  if (draft.step === 'tip') return renderTip(draft);
  if (draft.step === 'pay') return renderPay(draft);
  return renderReview(draft);
}

function renderScanning(d: Draft): void {
  document.title = titled('Reading receipt');
  app.innerHTML = `
    ${stepBar(`<a class="back" href="/" data-go>${icon.back}Back</a>`, d.even, 'Bill')}
    <div class="body">
      ${d.photoUrl ? `<div class="scan-photo"><img src="${d.photoUrl}" alt="Your receipt"></div>` : ''}
      <h1 class="h"><span class="spinner"></span>Reading your receipt…</h1>
      <p class="lede">Usually a few seconds. You'll check every line next.</p>
    </div>`;
}

function renderReview(d: Draft): void {
  document.title = titled('Check the receipt');
  app.innerHTML = `
    ${stepBar(`<a class="back" href="/" data-go>${icon.back}Start over</a>`, d.even, 'Bill')}
    <div class="body">
      <div class="stack" style="gap:6px">
        <h1 class="h">Check the bill</h1>
        <p class="lede">Make sure we read your receipt right.</p>
      </div>
      ${d.error ? `<div class="error">${esc(d.error)}</div>` : ''}
      <div id="gap"></div>
      ${d.photoUrl ? `<img class="photo-thumb" src="${d.photoUrl}" alt="Your receipt photo. Tap to enlarge." data-act="photo">` : ''}
      ${d.note ? `<div class="note">${esc(d.note)}</div>` : ''}
      <div class="field">
        <label for="venue">Restaurant</label>
        <input id="venue" class="input" data-f="venue" value="${esc(d.venue)}" placeholder="Restaurant name">
      </div>
      <span class="lbl" style="margin-bottom:-10px">${esc(d.date)} · Tap a line to fix it</span>
      <div>
        ${d.items.map((it) => `
          <div class="edit-row">
            <input class="name" data-k="${it.key}" data-f="name" value="${esc(it.name)}" placeholder="Item" aria-label="Item name">
            <input class="price" data-k="${it.key}" data-f="cents" value="${it.cents ? plain(it.cents) : ''}" placeholder="0.00" inputmode="decimal" aria-label="Price">
            <button class="icon-btn" data-act="del" data-k="${it.key}" aria-label="Remove ${esc(it.name || 'item')}">${icon.x}</button>
          </div>
          ${it.maybeFee ? `
            <div class="alert" style="padding:8px 12px;gap:8px;flex-wrap:wrap;margin:2px 0 6px">
              <span style="flex:1 1 100%">Looks like it might be a fee, not something someone ordered.</span>
              <button class="small-btn" data-act="toFee" data-k="${it.key}">Move to fees</button>
              <button class="small-btn" data-act="notFee" data-k="${it.key}" style="background:var(--card);color:var(--ink);border:1px solid var(--line)">It's food</button>
            </div>` : ''}`).join('')}
        ${addLink('add', '+ Add an item', 'Someone claims it, like a dish or a drink')}
        ${addLink('addFee', '+ Add a fee', 'Everyone shares it by what they ordered, like tax. For a card surcharge or service fee.')}
      </div>
      <div class="card stack">
        <div class="edit-row" style="border:0;padding:0">
          <label for="tax" style="flex:1;font-weight:600">Tax</label>
          <input id="tax" class="price input" style="width:110px;height:44px" data-f="tax" value="${d.taxCents ? plain(d.taxCents) : ''}" placeholder="0.00" inputmode="decimal">
        </div>
        ${d.fees.length ? `
          <div class="stack" style="gap:0">
            <span style="font-weight:600">Fees</span>
            <span class="sub">Shared by everyone by what they ordered, like tax</span>
            ${d.fees.map((f) => `
              <div class="edit-row">
                <input class="name" data-k="${f.key}" data-f="feeName" value="${esc(f.name)}" placeholder="Fee" aria-label="Fee name">
                <input class="price" data-k="${f.key}" data-f="feeCents" value="${f.cents ? plain(f.cents) : ''}" placeholder="0.00" inputmode="decimal" aria-label="Fee amount">
                <button class="icon-btn" data-act="delFee" data-k="${f.key}" aria-label="Remove ${esc(f.name || 'fee')}">${icon.x}</button>
              </div>
              <button class="link" data-act="toItem" data-k="${f.key}" style="align-self:flex-start;padding:2px 0 8px;font-size:13px">Not a fee? Move to items</button>`).join('')}
          </div>` : ''}
      </div>
      <div class="ledger" id="sum"></div>
    </div>
    <div class="foot"><button class="btn" data-act="next">Looks right</button></div>`;
  renderSummary(d);

  app.oninput = (e) => {
    const el = e.target;
    if (!(el instanceof HTMLInputElement) || !el.dataset.f) return;
    const it = d.items.find((x) => x.key === el.dataset.k);
    switch (el.dataset.f) {
      case 'venue': d.venue = el.value; break;
      case 'name': if (it) it.name = el.value; break;
      case 'cents': if (it) it.cents = toCents(el.value); break;
      case 'tax': d.taxCents = toCents(el.value); break;
      case 'feeName': { const f = d.fees.find((x) => x.key === el.dataset.k); if (f) f.name = el.value; break; }
      case 'feeCents': { const f = d.fees.find((x) => x.key === el.dataset.k); if (f) f.cents = toCents(el.value); break; }
    }
    renderSummary(d);
  };
  app.onclick = (e) => {
    const b = target(e, '[data-act]');
    if (!b) return;
    switch (b.dataset.act) {
      case 'add': {
        const it = blankItem();
        d.items.push(it);
        renderReview(d);
        app.querySelector<HTMLInputElement>(`[data-k="${it.key}"][data-f="name"]`)?.focus();
        break;
      }
      case 'del':
        d.items = d.items.filter((x) => x.key !== b.dataset.k);
        if (!d.items.length) d.items.push(blankItem());
        renderReview(d);
        break;
      case 'gapFee': {
        const g = receiptGap(d);
        if (g && g.diff > 0) d.fees.push({ key: nextKey(), name: 'Fee', cents: g.diff });
        renderReview(d);
        break;
      }
      case 'addFee': {
        const f = { key: nextKey(), name: '', cents: 0 };
        d.fees.push(f);
        renderReview(d);
        app.querySelector<HTMLInputElement>(`[data-k="${f.key}"][data-f="feeName"]`)?.focus();
        break;
      }
      case 'toFee': {
        const it = d.items.find((x) => x.key === b.dataset.k);
        if (it) {
          d.items = d.items.filter((x) => x !== it);
          if (!d.items.length) d.items.push(blankItem());
          d.fees.push({ key: it.key, name: it.name, cents: it.cents });
        }
        renderReview(d);
        break;
      }
      case 'notFee': {
        const it = d.items.find((x) => x.key === b.dataset.k);
        if (it) it.maybeFee = false;
        renderReview(d);
        break;
      }
      case 'toItem': {
        const f = d.fees.find((x) => x.key === b.dataset.k);
        if (f) {
          d.fees = d.fees.filter((x) => x !== f);
          d.items.push({ key: f.key, name: f.name, cents: f.cents });
        }
        renderReview(d);
        break;
      }
      case 'delFee':
        d.fees = d.fees.filter((x) => x.key !== b.dataset.k);
        renderReview(d);
        break;
      case 'gapLine': {
        const g = receiptGap(d);
        if (!g || g.diff <= 0) break;
        const it = { key: nextKey(), name: 'Missing item', cents: g.diff };
        d.items.push(it);
        renderReview(d);
        const name = app.querySelector<HTMLInputElement>(`[data-k="${it.key}"][data-f="name"]`);
        name?.focus();
        name?.select();
        break;
      }
      case 'gapEdit': {
        const first = app.querySelector<HTMLInputElement>('.edit-row .price');
        first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        first?.focus();
        break;
      }
      case 'gapHide':
        d.gapHidden = true;
        renderSummary(d);
        break;
      case 'reread':
        void rereadReceipt(d);
        break;
      case 'photo':
        openSheet(`<img src="${d.photoUrl}" alt="Your receipt" style="width:100%;border-radius:12px"><button class="btn2" data-close>Close</button>`);
        break;
      case 'next': {
        const real = d.items.filter((x) => x.cents !== 0);
        if (!real.length) return toast('Add at least one item with a price.');
        // Don't let a misread slip through: fix it, or say "Leave it as is" first.
        if (receiptGap(d) && !d.gapHidden) {
          document.getElementById('gap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return toast("This doesn't match your receipt yet. Pick a fix above.");
        }
        d.items = real.map((x) => ({ ...x, name: x.name.trim() || 'Item' }));
        trackStep('receipt_checked', { source: d.flow.source, secs: Math.round((Date.now() - d.flow.startedAt) / 1000), items: d.items.length });
        toStep(d, 'people');
        break;
      }
    }
  };
}

function renderSummary(d: Draft): void {
  const el = document.getElementById('sum');
  if (!el) return;
  const sub = subtotal(d);
  let check = '';
  const { subtotalCents, totalCents } = d.receipt;
  if (subtotalCents != null || totalCents != null) {
    check = !receiptGap(d)
      ? `<div class="lead accent" style="padding-top:8px"><span>Lines add up to the receipt ${totalCents != null ? 'total' : 'subtotal'}</span></div>`
      : `<div class="lead warn" style="padding-top:8px;white-space:normal"><span>${d.gapHidden ? 'Doesn\'t match the receipt. Continuing as is.' : 'Doesn\'t add up yet. See the note at the top.'}</span></div>`;
  }
  renderGapBanner(d);
  el.innerHTML = leadCents('Subtotal', sub) + leadCents('Tax', d.taxCents) + (d.fees.length ? leadCents('Fees', feesTotal(d)) : '')
    + (d.receipt.serviceCents ? leadCents('Service charge', d.receipt.serviceCents) : '')
    + leadCents('Before the tip', billBeforeTip(d), 'total') + check;
}

function toStep(d: Draft, step: Draft['step']): void {
  d.step = step;
  d.setupError = '';
  window.scrollTo(0, 0);
  renderNew();
}

/** Name and at least one way to be paid, saved from last time. Then that step is skipped. */
const payReady = (p: PayerDetails): boolean => Boolean(p.name && (p.venmo || p.cashapp || p.zelle));
const payLine = (p: PayerDetails): string =>
  p.venmo ? `Venmo @${p.venmo}` : p.cashapp ? `Cash App $${p.cashapp}` : `Zelle ${p.zelle}`;
/** The pay step shows when it's the first time, or when the payer asked to change it. */
const needsPayStep = (d: Draft): boolean => d.editPay || !payReady(saved.profile());

// ---------- step 2: how many people ----------

function renderPeople(d: Draft): void {
  document.title = titled('How many people');
  app.innerHTML = `
    ${stepBar(`<button class="back" data-act="back">${icon.back}Back</button>`, d.even, 'People')}
    <div class="body">
      <div class="stack" style="gap:6px">
        <h1 class="h">How many people ate?</h1>
        <p class="lede">Count everyone at the table. You too.</p>
      </div>
      <div style="display:flex;justify-content:center;padding:8px 0">${stepper(d.partySize).replace('class="stepper"', 'class="stepper big"')}</div>
      <div class="stack" role="radiogroup" aria-label="How everyone pays you back" style="gap:8px">
        <span class="lbl">How do they pay you back?</span>
        <button type="button" class="opt" role="radio" aria-checked="${!d.even}" data-act="even" data-on="0"><span class="ring"></span><span style="flex:1"><b>Everyone pays for what they had</b><span class="sub">Friends tap their own items on their phone.</span></span></button>
        <button type="button" class="opt" role="radio" aria-checked="${d.even}" data-act="even" data-on="1"><span class="ring"></span><span style="flex:1"><b>Everyone pays the same</b><span class="sub">The whole bill split ${d.partySize} ways, tax and tip included. Nobody taps items.</span></span></button>
      </div>
    </div>
    <div class="foot"><button class="btn" data-act="next">Next</button></div>`;

  app.oninput = null;
  app.onclick = (e) => {
    const b = target(e, '[data-act]');
    if (!b) return;
    switch (b.dataset.act) {
      case 'back': return toStep(d, 'review');
      case 'size': d.partySize = clampSize(d.partySize + Number(b.dataset.d)); return renderPeople(d);
      case 'even': d.even = b.dataset.on === '1'; return renderPeople(d);
      case 'next': return toStep(d, 'tip');
    }
  };
}

// ---------- step 3: the tip ----------

function renderTip(d: Draft): void {
  document.title = titled('Pick the tip');
  const tips = [15, 18, 20, 22];
  const p = saved.profile();
  const last = !needsPayStep(d);
  // While the fee test is on, its group decides the thanks, so the switch gives way to the price line.
  // With pricing off there's no $1 thanks at all (the server drops it), so no switch either.
  const feeNote = payerFeeNote(d.partySize);
  const thanksSwitch = thanksSwitchShown(fee.pricing, d.partySize, feeNote);
  checkLinkCode(d);
  app.innerHTML = `
    ${stepBar(`<button class="back" data-act="back">${icon.back}Back</button>`, d.even, 'Tip')}
    <div class="body">
      <div class="stack" style="gap:6px">
        <h1 class="h">Pick the tip</h1>
        <p class="lede">${d.tip.label ? esc(d.tip.label) + '.' : 'For the whole table. Everyone pays their part of it.'}</p>
      </div>
      <div class="chips big">
        ${tips.map((t) => `<button class="chip" data-act="tip" data-p="${t}" aria-pressed="${d.tipSource !== 'no_tip' && d.tip.mode === 'pct' && d.tip.pct === t}">${t}%</button>`).join('')}
        <button class="chip" data-act="tip" data-p="amount" aria-pressed="${d.tip.mode === 'amount' && d.tipSource !== 'no_tip'}" aria-label="Type an amount">$</button>
        <button class="chip" data-act="noTip" id="noTip" aria-pressed="${d.tipSource === 'no_tip'}">No tip</button>
      </div>
      ${d.tip.mode === 'pct' ? `
        <div class="chips" role="radiogroup" aria-label="Tip on">
          <button class="chip" role="radio" data-act="tipOn" data-on="food" aria-pressed="${d.tip.on === 'food'}" style="flex:1">on food <span class="amt">${fmt(tipBase(d, 'food'))}</span></button>
          <button class="chip" role="radio" data-act="tipOn" data-on="total" aria-pressed="${d.tip.on === 'total'}" style="flex:1">on total with tax <span class="amt">${fmt(tipBase(d, 'total'))}</span></button>
        </div>` : `<input id="tipAmount" class="input amt" data-f="tipAmount" value="${d.tip.mode === 'amount' && d.tip.cents ? plain(d.tip.cents) : ''}" placeholder="Tip amount" inputmode="decimal" aria-label="Tip amount">`}
      <div class="card" style="border:2px solid var(--accent)" aria-live="polite">
        <div class="lbl" style="padding-bottom:6px">Write this on the slip</div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><span style="font-weight:600">Tip</span><span class="amt" id="slipTip" style="font-size:28px;font-weight:700">${fmt(slip(d).tip)}</span></div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><span style="font-weight:600">Total</span><span class="amt" id="slipTotal" style="font-size:28px;font-weight:700">${fmt(slip(d).total)}</span></div>
      </div>
      ${feeNote ? `<div class="note" id="feeNote">${esc(feeNote)}</div>` : !thanksSwitch ? '' : `
      <div class="card" style="display:flex;gap:14px;align-items:center">
        <div style="flex:1"><div style="font-weight:600">$1 thanks from each friend</div><div class="sub">${d.thanksOn ? 'On. Friends see it as its own line. You keep it.' : 'Off for this split. Friends pay only their share.'}</div></div>
        <button type="button" class="switch" role="switch" aria-checked="${d.thanksOn}" aria-label="$1 thanks from each friend" data-act="thanks"><span></span></button>
      </div>`}
      ${d.even ? `<div class="note" id="evenNote">Split ${d.partySize} ways, that's <b>${fmt(evenEach(d))}</b> each, tip included.</div>` : ''}
      ${d.code.offer !== undefined && d.code.fromLink ? `<div class="note">Code <b>${esc(d.code.value)}</b> applied${d.code.offer ? `: ${esc(d.code.offer)}` : ''}.</div>` : ''}
      ${last ? `<div class="sub">Friends pay you on ${esc(payLine(p))}. <button class="link" data-act="editPay" style="padding:0">Change</button></div>` : ''}
      ${d.setupError ? `<div class="error">${esc(d.setupError)}</div>` : ''}
    </div>
    <div class="foot"><button class="btn" data-act="next" id="${last ? 'createBtn' : 'tipNext'}">${last ? 'Make my code' : 'Next'}</button></div>`;

  const refresh = () => {
    const sl = slip(d);
    const set = (id: string, v: string) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    set('slipTip', fmt(sl.tip));
    set('slipTotal', fmt(sl.total));
    const note = document.getElementById('evenNote');
    if (note) note.innerHTML = `Split ${d.partySize} ways, that's <b>${fmt(evenEach(d))}</b> each, tip included.`;
  };
  app.oninput = (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.dataset.f === 'tipAmount') { d.tip = { mode: 'amount', cents: toCents(el.value), label: '' }; d.tipSource = 'host_pick'; refresh(); }
  };
  app.onclick = async (e) => {
    const b = target<HTMLButtonElement>(e, '[data-act]');
    if (!b) return;
    switch (b.dataset.act) {
      case 'back': return toStep(d, 'people');
      case 'noTip':
        d.tip = { mode: 'amount', cents: 0, label: 'No tip' };
        d.tipSource = 'no_tip';
        return renderTip(d);
      case 'tip':
        d.tipSource = 'host_pick';
        d.tip = b.dataset.p === 'amount'
          ? { mode: 'amount', cents: d.tip.mode === 'amount' ? d.tip.cents : tipCents(d), label: d.tip.label }
          : { mode: 'pct', pct: Number(b.dataset.p), on: d.tip.mode === 'pct' ? d.tip.on : saved.tipOn(), label: '' };
        renderTip(d);
        if (b.dataset.p === 'amount') (document.getElementById('tipAmount') as HTMLInputElement | null)?.focus();
        return;
      case 'tipOn':
        if (d.tip.mode === 'pct') {
          const on = b.dataset.on === 'total' ? 'total' : 'food';
          d.tip = { ...d.tip, on };
          saved.setTipOn(on);
        }
        return renderTip(d);
      case 'editPay': d.editPay = true; return toStep(d, 'pay');
      case 'thanks': d.thanksOn = !d.thanksOn; if (d.thanksOn) d.thanksPicked = true; return renderTip(d);
      case 'next':
        if (!last) return toStep(d, 'pay');
        return create(d, saved.profile(), b);
    }
  };
}

/** A code from a link: check it once and show what it gives. */
function checkLinkCode(d: Draft): void {
  const fromLink = linkCode();
  if (!fromLink || d.code.checked || d.code.value) return;
  d.code.checked = true;
  checkCode(fromLink)
    .then((c) => {
      // Their own code: it won't apply to their split, so say nothing (no "Code ... applied.").
      if (c.own) return;
      d.code = { value: c.code, open: false, offer: c.offer, fromLink: true, checked: true };
      if (draft === d && (d.step === 'tip' || d.step === 'pay')) renderNew();
    })
    .catch(() => { /* expired or unknown: say nothing, they can type one */ });
}

// ---------- step 4, the first time: where friends pay you ----------

function renderPay(d: Draft): void {
  document.title = titled('How friends pay you');
  const p = saved.profile();
  checkLinkCode(d);
  app.innerHTML = `
    ${stepBar(`<button class="back" data-act="back">${icon.back}Back</button>`, d.even, 'Share')}
    <form class="body" id="setup" autocomplete="on">
      <div class="stack" style="gap:6px">
        <h1 class="h">Where do friends pay you?</h1>
        <p class="lede">Saved on this phone, so you only do this once.</p>
      </div>
      <div class="field"><label for="nm">Your name</label><input id="nm" class="input" name="name" value="${esc(p.name)}" autocomplete="given-name" required></div>
      <div class="field"><label for="vm">Venmo username</label><input id="vm" class="input" name="venmo" value="${esc(p.venmo)}" placeholder="@your-name" autocapitalize="none"></div>
      <div class="field"><label for="ca">Cash App $cashtag</label><input id="ca" class="input" name="cashapp" value="${esc(p.cashapp)}" placeholder="$yourtag" autocapitalize="none"></div>
      <div class="field"><label for="zl">Zelle phone or email <span class="sub">(optional)</span></label><input id="zl" class="input" name="zelle" value="${esc(p.zelle)}" autocapitalize="none"><span class="hint">Zelle can't fill in an amount, so friends copy this and type it.</span></div>
      ${d.code.offer !== undefined && d.code.fromLink
        ? `<div class="note">Code <b>${esc(d.code.value)}</b> applied${d.code.offer ? `: ${esc(d.code.offer)}` : ''}.</div>`
        : d.code.open
          ? `<div class="field"><label for="refCode">Promo or referral code</label><input id="refCode" class="input amt" name="refCode" value="${esc(d.code.value)}" autocapitalize="characters" autocomplete="off" maxlength="20" placeholder="ALEX10"></div>`
          : '<button type="button" class="link" data-act="code" style="align-self:flex-start">Have a code?</button>'}
      ${d.setupError ? `<div class="error">${esc(d.setupError)}</div>` : ''}
    </form>
    <div class="foot"><button class="btn" data-act="create" id="createBtn">Make my code</button></div>`;

  const read = (): PayerDetails => {
    const f = document.getElementById('setup') as HTMLFormElement;
    const val = (name: string) => (f.elements.namedItem(name) as HTMLInputElement).value.trim();
    return { name: val('name'), venmo: val('venmo').replace(/^@/, ''), cashapp: val('cashapp').replace(/^\$/, ''), zelle: val('zelle') };
  };
  const readCode = () => { const el = document.getElementById('refCode') as HTMLInputElement | null; if (el) d.code.value = el.value.trim().toUpperCase(); };

  app.oninput = null;
  app.onsubmit = (e) => e.preventDefault();
  app.onclick = async (e) => {
    const b = target<HTMLButtonElement>(e, '[data-act]');
    if (!b) return;
    const act = b.dataset.act;
    if (act === 'code') { saved.setProfile(read()); d.code.open = true; renderPay(d); (document.getElementById('refCode') as HTMLInputElement | null)?.focus(); return; }
    readCode();
    if (act === 'back') {
      saved.setProfile(read());
      return toStep(d, 'tip');
    }
    if (act !== 'create') return;
    const payer = read();
    saved.setProfile(payer);
    if (!payer.name) { d.setupError = 'Add your name so friends know who to pay.'; return renderPay(d); }
    if (!nameLongEnough(payer.name)) { d.setupError = NAME_TOO_SHORT; return renderPay(d); }
    if (!payer.venmo && !payer.cashapp && !payer.zelle) { d.setupError = 'Add Venmo, Cash App or Zelle so friends can pay you.'; return renderPay(d); }
    await create(d, payer, b);
  };
}

/** Make the split and go to the payer's page, which opens on the code to show friends. */
async function create(d: Draft, payer: PayerDetails, b: HTMLButtonElement): Promise<void> {
  b.disabled = true;
  b.textContent = 'Making your code…';
  try {
    const res = await createSplit({
      venue: d.venue.trim() || UNNAMED_VENUE, date: d.date,
      items: d.items.map((x) => ({ name: x.name, cents: x.cents })),
      // Fees ride along with tax: both are split in proportion to what each person had.
      taxCents: d.taxCents + feesTotal(d), taxLabel: feesTotal(d) ? 'Tax & fees' : '', tipCents: tipCents(d), tipSource: d.tipSource,
      fees: d.fees.filter((f) => f.cents > 0).map((f) => ({ name: f.name.trim() || 'Fee', cents: f.cents })),
      tipLabel: d.tip.mode === 'amount' ? (d.tip.label || 'Tip') : d.tip.on === 'total' ? `${d.tip.pct}% of total` : `${d.tip.pct}%`,
      thanksOn: d.thanksOn && d.partySize >= FEE_MIN_TABLE, thanksPicked: d.thanksOn && d.thanksPicked === true, partySize: d.partySize, even: d.even, payer,
      refCode: d.code.fromLink ? '' : d.code.value,
      place: d.place,
    }, { source: d.flow.source, secs: Math.round((Date.now() - d.flow.startedAt) / 1000), rereads: d.flow.rereads });
    saved.setAdminKey(res.code, res.adminKey);
    saved.setMembership(res.code, { id: res.personId, token: res.token });
    saved.setFresh(res.code, true);
    saved.setStage(res.code, 'share');
    draft = null;
    go(`/p/${res.code}#k=${res.adminKey}`);
  } catch (err) {
    d.setupError = (err as Error).message;
    renderNew();
  }
}
